import * as Sentry from '@sentry/nextjs';
import {BACKEND_URL, ENVIRONMENT} from 'config';
import {IncomingHttpHeaders} from 'http';

export const API = {
  fetch: async ({
    route,
    method,
    body,
    formData,
    keepalive,
    cache,
    next,
  }: {
    route: string;
    method: string;
    body?: object;
    formData?: FormData;
    // Let the request complete even if the page is unloading (e.g. tracking an
    // event right before a navigation/redirect).
    keepalive?: boolean;
    // Next.js Data Cache passthrough — lets a server-side GET opt into fetch-level
    // caching (e.g. `next: {revalidate: 43200}`) or explicitly opt out
    // (`cache: 'no-store'`) independently of whether the route itself is dynamic.
    cache?: RequestCache;
    next?: NextFetchRequestConfig;
  }): Promise<any> => {
    const start = performance.now();
    let response: Response;
    try {
      response = await fetch(`${BACKEND_URL}${route}`, {
        method,
        headers: API._headers,
        credentials: 'include',
        body: body ? JSON.stringify(body) : formData,
        keepalive,
        cache,
        next,
      });
    } catch (error) {
      const fetchError = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(fetchError, {extra: {route, method}});
      API.onFetchError(fetchError);
      throw fetchError;
    }

    const end = performance.now();
    if (ENVIRONMENT !== 'production') {
      console.log(`[API] ${route} — ${Math.round(end - start)}ms`);
    }

    // If the backend returned this header then we need to reload the user permissions on the client.
    if (response.headers.get('x-update-permissions')) {
      API.onReloadPermissions();
    }

    if (response.headers.get('x-update-badge')) {
      API.onReloadBadge();
    }

    if (response.headers.get('x-update-stats')) {
      API.onReloadStats();
    }

    // Parse response body.
    let responseBody;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json') || contentType.includes('text/plain')) {
      responseBody = response.status !== 204 ? await response.json() : {};
    }

    // Handle 5xx errors.
    if (response.status >= 500) {
      const error = new Error(responseBody?.error || 'Internal server error');
      Sentry.captureException(error, {extra: {route, method, status: response.status}});
      API.onFetchError(error);
      throw error;
    }

    return responseBody !== undefined ? responseBody : response;
  },

  // Default no-ops. _app.tsx and AuthContext configure these callbacks when initializing.
  onFetchError: (error: Error) => {},

  onReloadPermissions: async () => {},

  onReloadBadge: async () => {},

  onReloadStats: async () => {},

  // Helper method to make an API call with some extra headers.
  _headers: {} as any,
  withHeaders: async <T>(headers: IncomingHttpHeaders, execute: () => Promise<T>) => {
    API._headers = {};
    for (let h of ['cookie', 'x-forwarded-for', 'user-agent']) {
      if (headers[h]) API._headers[h] = headers[h];
    }

    const result = await execute();
    API._headers = {};
    return result;
  },

  get: (route: string, opts: {cache?: RequestCache; next?: NextFetchRequestConfig} = {}) => {
    return API.fetch({route, method: 'GET', ...opts});
  },

  post: (route: string, body: any, opts: {keepalive?: boolean} = {}) => {
    return API.fetch({route, method: 'POST', body, keepalive: opts.keepalive});
  },

  postFile: (route: string, fileField: string, file: File) => {
    const formData = new FormData();
    formData.append(fileField, file, file.name);
    return API.fetch({route, method: 'POST', formData});
  },

  getFile: async (route: string) => {
    const response = await fetch(`${BACKEND_URL}${route}`, {
      method: 'GET',
      headers: API._headers,
      credentials: 'include',
    });
    return response.blob();
  },

  getFileStream: async (route: string): Promise<ReadableStream<Uint8Array>> => {
    const response = await fetch(`${BACKEND_URL}${route}`, {
      method: 'GET',
      headers: API._headers,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Failed to get reader from response body');
    }

    const stream = new ReadableStream<Uint8Array>({
      start(controller) {
        function push() {
          reader!
            .read()
            .then(({done, value}) => {
              if (done) {
                controller.close();
                return;
              }
              if (value) {
                controller.enqueue(value);
              }
              push();
            })
            .catch((error) => {
              console.error('Error reading stream', error);
              controller.error(error);
            });
        }
        push();
      },
    });

    return stream;
  },

  put: (route: string, body: any) => {
    return API.fetch({route, method: 'PUT', body});
  },

  patch: (route: string, body: any) => {
    return API.fetch({route, method: 'PATCH', body});
  },

  delete: (route: string) => {
    return API.fetch({route, method: 'DELETE'});
  },
};
